/**
 * Rich blocks drawn from what an agent wrote (D358), DOM-free and with no model.
 *
 * Agents keep writing the same few shapes in prose: a test result, a Markdown table, a list of files, a run of
 * checks, a plan that goes "Next … / Then …". A deterministic pass over their text recognises each by a precise
 * pattern and hands back the shape for the app to draw — stats, a table, a steps tracker, a file list with Open —
 * with everything else left as the Markdown it was. A wrong block is worse than plain text, so every recogniser
 * insists on the whole shape and gives up on anything it is not sure of: a list with one line that is not a path
 * stays a list; "5/5 pass under umask 0022" is not a result; a check whose outcome cannot be read is left alone.
 *
 * Nothing here draws or runs anything. The specs are `UiSpecV1` — the vocabulary agents' own blocks use, drawn by the
 * same renderer — and `conversations.richContent` (Settings, the organisation's policy) decides whether this pass
 * runs at all. Pinned by `webview-ui/test/derivedBlocks.test.ts` (there) and `derivedBlocks.test.ts` (here).
 */
/** Blocks taken from one text; the rest stays Markdown. */
export const MAX_DERIVED = 8;
const spec = (alt, root, title) => ({ v: 1, ...(title ? { title } : {}), alt: alt.slice(0, 480), root });
const num = (s) => Number(s.replace(/,/g, ''));
/** Inline Markdown a table cell or a label may carry, as plain text. */
const plain = (s) => s.replace(/\*\*|__/g, '').replace(/`/g, '').replace(/\\\|/g, '|').trim();
const upper = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
// ── Tables ────────────────────────────────────────────────────────────
const ROW = /^\s*\|.*\|\s*$/;
const SEPARATOR = /^\s*\|(?:\s*:?-+:?\s*\|)+\s*$/;
function cells(line) {
    const body = line.trim().replace(/^\|/, '').replace(/\|$/, '');
    const out = [];
    let cur = '';
    for (let i = 0; i < body.length; i++) {
        if (body[i] === '\\' && body[i + 1] === '|') {
            cur += '|';
            i++;
        }
        else if (body[i] === '|') {
            out.push(cur);
            cur = '';
        }
        else
            cur += body[i];
    }
    out.push(cur);
    return out.map((c) => plain(c));
}
function table(lines, i) {
    if (!ROW.test(lines[i]) || !SEPARATOR.test(lines[i + 1] ?? ''))
        return undefined;
    const head = cells(lines[i]);
    if (head.length < 2 || head.length > 12 || head.some((h) => h.length > 120))
        return undefined;
    if (cells(lines[i + 1].replace(/:?-+:?/g, 'x')).length !== head.length)
        return undefined;
    const rows = [];
    let j = i + 2;
    while (j < lines.length && ROW.test(lines[j])) {
        const row = cells(lines[j]);
        if (row.length !== head.length || row.some((c) => c.length > 240))
            return undefined;
        rows.push(Object.fromEntries(row.map((c, k) => [`c${k}`, c])));
        j++;
    }
    if (!rows.length || rows.length > 200)
        return undefined;
    const columns = head.map((label, k) => ({ key: `c${k}`, label }));
    const alt = `Table: ${head.join(', ')} (${rows.length} row${rows.length === 1 ? '' : 's'})`;
    return { used: j - i, segments: [{ kind: 'spec', recogniser: 'table', spec: spec(alt, { type: 'table', columns, rows }) }] };
}
// ── Checks: "Lint: clean", "Build: passed" ───────────────────────────────
const CHECK_LINE = /^\s*(?:[-*]\s+)?(?:\*\*)?(Checks?|Tests?|Lint(?:ing)?|Build|Typecheck|Type check|Compile|Docs|Format)(?:\*\*)?\s*:\s*(?:\*\*)?\s*(.+?)\s*$/i;
/** What a check's outcome says, or undefined when it is not plainly one of the six. */
export function checkState(value) {
    const v = plain(value).toLowerCase();
    if (/^(❌|✗|✘|fail|error|red\b|broken|not passing)/.test(v) || /\b[1-9]\d*\s+(failed|failures?|errors?)\b/.test(v))
        return 'failed';
    if (/^(skipped|not run|n\/a)\b/.test(v))
        return 'skipped';
    if (/^(running|in progress|pending|started|checking)\b/.test(v))
        return 'running';
    if (/^(✅|✓|✔|pass|clean\b|ok\b|green\b|success|succeeded|0 (errors?|failures?|failed|warnings?|findings|problems)\b)/.test(v))
        return 'done';
    if (/^\d[\d,]*(\s*\/\s*\d[\d,]*)?\s+(tests?\s+)?pass(ed|es)?\b/.test(v))
        return 'done';
    return undefined;
}
function checks(lines, i) {
    const steps = [];
    let j = i;
    while (j < lines.length) {
        const m = CHECK_LINE.exec(lines[j]);
        if (!m)
            break;
        const state = checkState(m[2]);
        const hint = plain(m[2]);
        if (!state || hint.length > 240)
            return undefined;
        steps.push({ label: upper(m[1].toLowerCase()), state, hint });
        j++;
    }
    if (steps.length < 2 || steps.length > 24)
        return undefined;
    const alt = steps.map((s) => `${s.label}: ${s.hint}`).join('; ');
    return { used: j - i, segments: [{ kind: 'spec', recogniser: 'checks', spec: spec(alt, { type: 'steps', steps }, 'Checks') }] };
}
// ── "Next I'll … / Then …" ─────────────────────────────────────────────
const NEXT_LINE = /^\s*(?:[-*]\s+|\d+[.)]\s+)?(?:\*\*)?(Next|Then|After that|Afterwards|Finally|Lastly)\b(?:\*\*)?[,:]?\s+(.+?)\s*$/i;
function stepLabel(rest) {
    const label = upper(plain(rest).replace(/^(?:up\s*(?:is|:)?\s*|is\s+|(?:i|we)(?:'ll|’ll| will)\s+)/i, '').replace(/[.;]+$/, '').trim());
    return label && label.length <= 120 ? label : undefined;
}
function nextSteps(lines, i) {
    const first = NEXT_LINE.exec(lines[i]);
    if (!first || first[1].toLowerCase() !== 'next')
        return undefined;
    const steps = [];
    let j = i;
    while (j < lines.length) {
        const m = NEXT_LINE.exec(lines[j]);
        if (!m || (j > i && m[1].toLowerCase() === 'next'))
            break;
        const label = stepLabel(m[2]);
        if (!label)
            return undefined;
        steps.push({ label, state: 'todo' });
        j++;
    }
    if (steps.length < 3 || steps.length > 24)
        return undefined;
    return { used: j - i, segments: [{ kind: 'spec', recogniser: 'steps', spec: spec(`What happens next: ${steps.map((s) => s.label).join(', then ')}`, { type: 'steps', steps }, 'What happens next') }] };
}
// ── Files ──────────────────────────────────────────────────────────────
const FILE_EXT = /\.(?:tsx?|jsx?|mjs|cjs|json|md|css|html|ya?ml|py|rs|go|sh|ps1|txt|toml|lock|sql|cs|java|kt|swift)$/i;
const PATH = /^(?:\.{1,2}\/)?[\w@.+-]+(?:\/[\w@.+-]+)*\.[A-Za-z0-9]{1,8}$/;
const FILE_ITEM_TICKED = /^\s*(?:[-*]\s+|\d+[.)]\s+)(?:\*\*)?`([^`\s]+)`(?:\*\*)?\s*(?:(?:[—–:]|-{1,2})\s+(.+?))?\s*$/;
const FILE_ITEM_BARE = /^\s*(?:[-*]\s+|\d+[.)]\s+)([^\s`]+)\s*$/;
function files(lines, i) {
    const out = [];
    let j = i;
    while (j < lines.length && /^\s*(?:[-*]|\d+[.)])\s+\S/.test(lines[j])) {
        const ticked = FILE_ITEM_TICKED.exec(lines[j]);
        let path;
        let note;
        if (ticked) {
            path = ticked[1].replace(/:\d+(?::\d+)?$/, '');
            note = ticked[2] ? plain(ticked[2]) : undefined;
            if (!PATH.test(path) || !(path.includes('/') || FILE_EXT.test(path)))
                return undefined;
        }
        else {
            const bare = FILE_ITEM_BARE.exec(lines[j]);
            path = bare?.[1].replace(/:\d+(?::\d+)?$/, '');
            if (!path || !path.includes('/') || !PATH.test(path) || !FILE_EXT.test(path) || /^https?:/.test(path))
                return undefined;
        }
        if (note && note.length > 240)
            return undefined;
        out.push({ path, ...(note ? { note } : {}) });
        j++;
    }
    // A list the shape does not cover whole is not a file list, whatever some of its items are.
    if (out.length < 2 || out.length > 40)
        return undefined;
    return { used: j - i, segments: [{ kind: 'files', files: out }] };
}
// ── Test results ───────────────────────────────────────────────────────
const LEAD = String.raw `^\s*(?:[-*]\s+)?(?:\*\*)?(?:(?:Tests?|Results?)\s*:\s*)?(?:\*\*)?`;
const R_OF = new RegExp(`${LEAD}(\\d[\\d,]*)\\s*(?:/|of)\\s*(\\d[\\d,]*)(?:\\s+(?:tests?|specs?|checks?))?\\s+pass(?:ed|es|ing)?(?:\\*\\*)?`, 'i');
const R_PF = new RegExp(`${LEAD}(\\d[\\d,]*)\\s+pass(?:ed|es|ing)?\\s*(?:,|and)\\s*(\\d[\\d,]*)\\s+fail(?:ed|ures?|ing)?(?:\\*\\*)?`, 'i');
const R_IN = /^\s*(?:[-*]\s+)?(?:\*\*)?(\d[\d,]*)\s+(?:tests?\s+)?fail(?:ed|ures?|ing)?\s+in\s+(.+?)[.:]?(?:\*\*)?\s*$/i;
const stat = (label, value, tone, hint) => ({ type: 'stat', label, value, tone, ...(hint ? { hint } : {}) });
const statRow = (alt, stats) => ({ kind: 'spec', recogniser: 'tests', spec: spec(alt, { type: 'stack', children: stats }) });
function testResult(lines, i) {
    const line = lines[i];
    if (line.length > 400)
        return undefined;
    const inline = R_IN.exec(line);
    if (inline && line.length <= 140) {
        const n = num(inline[1]);
        const where = plain(inline[2]);
        if (n < 1 || where.length > 240)
            return undefined;
        return { used: 1, segments: [statRow(`${n} failing in ${where}`, [stat('Failing', n, 'rose', `in ${where}`)])] };
    }
    const of = R_OF.exec(line);
    const m = of ?? R_PF.exec(line);
    if (!m)
        return undefined;
    const after = line.slice(m[0].length).trim();
    // The result must end its sentence: "…pass under umask 0022" and "…pass, so it works" are prose, not a result.
    if (after && !/^[.:]\s+\S|^[.:]$/.test(after))
        return undefined;
    const rest = after.replace(/^[.:]\s*/, '');
    const a = num(m[1]);
    const b = num(m[2]);
    let segment;
    if (!of) {
        segment = statRow(`${a} passed, ${b} failed`, [stat('Passed', a, 'green'), stat('Failed', b, b > 0 ? 'rose' : 'slate')]);
    }
    else {
        if (a > b || b === 0)
            return undefined;
        const stats = [stat('Passing', `${a} of ${b}`, a === b ? 'green' : 'amber')];
        if (b > a)
            stats.push(stat('Not passing', b - a, 'rose'));
        segment = statRow(`${a} of ${b} pass`, stats);
    }
    return { used: 1, segments: [segment], ...(rest ? { rest } : {}) };
}
// ── The pass ───────────────────────────────────────────────────────────
const RECOGNISERS = [table, checks, nextSteps, files, testResult];
/**
 * An agent's text as Markdown and the blocks drawn from it, in order. Fenced code is never read. `enabled` false (the
 * switch, the policy) returns the text as one Markdown segment.
 */
export function deriveBlocks(text, opts = {}) {
    if (opts.enabled === false || !text.trim())
        return [{ kind: 'markdown', text }];
    const lines = text.split('\n');
    const out = [];
    let buffer = [];
    let fence;
    let derived = 0;
    const flush = () => {
        if (buffer.length) {
            const t = buffer.join('\n');
            if (t.trim())
                out.push({ kind: 'markdown', text: t });
            buffer = [];
        }
    };
    for (let i = 0; i < lines.length;) {
        const line = lines[i];
        const f = /^\s*(```+|~~~+)/.exec(line);
        if (fence) {
            buffer.push(line);
            if (f && f[1].startsWith(fence[0]) && f[1].length >= fence.length)
                fence = undefined;
            i++;
            continue;
        }
        if (f) {
            fence = f[1];
            buffer.push(line);
            i++;
            continue;
        }
        let found;
        if (derived < MAX_DERIVED && line.trim()) {
            for (const recognise of RECOGNISERS) {
                found = recognise(lines, i);
                if (found)
                    break;
            }
        }
        if (!found) {
            buffer.push(line);
            i++;
            continue;
        }
        flush();
        out.push(...found.segments);
        derived++;
        i += found.used;
        if (found.rest)
            buffer.push(found.rest);
    }
    flush();
    return out.length ? out : [{ kind: 'markdown', text }];
}
/** Whether `deriveBlocks` found anything to draw. */
export const hasDerived = (segments) => segments.some((s) => s.kind !== 'markdown');
//# sourceMappingURL=derivedBlocks.js.map